// Settings → Competitor Playbook tab.
//
// The DEFAULT playbook (TKOS trade intelligence) is shown read-only; the
// retailer adds their OWN talking points per competitor, can switch a default
// off, and can add competitors not on the list (e.g. local rivals). When a
// customer names a competitor, Kit uses the default tip + the retailer's points
// to draft a response — so every retailer's trade knowledge goes into their Kit.
//
// Platform-only: this is the retailer's own strategy text — no customer data.
// Backend (migration 0062, RLS-scoped):
//   GET    /api/portal/competitor-playbook
//   POST   /api/portal/competitor-playbook         { label, aliases?, talkingPoints? }
//   PUT    /api/portal/competitor-playbook/:key     { talkingPoints?, enabled?, label?, aliases? }
//   DELETE /api/portal/competitor-playbook/:key

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";

import type { SettingsTab } from "../types";
import type { PageContext } from "../../../shell/types";

import "./competitor-playbook.css";

interface PBEntry {
  key: string;
  label: string;
  aliases: string[];
  tip: string;
  weaknesses: string[];
  talkingPoints: string;
  enabled: boolean;
  isCustom: boolean;
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

const competitorPlaybookTab: SettingsTab = {
  id: "competitor-playbook",
  label: "Competitor Playbook",
  icon: iconMarkup("chart", { size: "sm" }),
  mount(container: HTMLElement, ctx: PageContext) {
    const ac = new AbortController();
    let disposed = false;
    let entries: PBEntry[] = [];

    container.innerHTML = `
      <section class="set-cp-root" aria-labelledby="set-cp-h">
        <h2 id="set-cp-h" class="set-section-title">Competitor playbook</h2>
        <p class="set-help">
          When a customer mentions a competitor, Kit surfaces a response strategy and can
          draft a reply. Below are the built-in TKOS strategies — add your own local knowledge
          to any of them, switch off ones you never compete with, or add competitors of your own.
          Your notes are private to your business.
        </p>
        <div class="set-cp-list" data-role="list">Loading…</div>

        <div class="set-cp-add">
          <h3 class="set-cp-add-title">Add a competitor</h3>
          <div class="set-field">
            <label for="set-cp-name">Name</label>
            <input id="set-cp-name" class="set-input" type="text" maxlength="80"
              placeholder="e.g. a local rival" data-role="add-name" />
          </div>
          <div class="set-field">
            <label for="set-cp-aliases">Also matches (comma-separated, optional)</label>
            <input id="set-cp-aliases" class="set-input" type="text" maxlength="200"
              placeholder="e.g. Smith Kitchens, Smiths" data-role="add-aliases" />
          </div>
          <div class="set-field">
            <label for="set-cp-points">Your talking points</label>
            <textarea id="set-cp-points" class="set-input set-cp-textarea" rows="3" maxlength="4000"
              placeholder="What to remind the customer about your service vs theirs…" data-role="add-points"></textarea>
          </div>
          <p class="set-cp-error" data-role="add-error" hidden></p>
          <button type="button" class="set-btn" data-role="add-save">Add competitor</button>
        </div>
      </section>
    `;

    const listEl = container.querySelector<HTMLElement>('[data-role="list"]')!;

    function cardHtml(e: PBEntry): string {
      const tipHtml = e.tip
        ? `<p class="set-cp-tip">${esc(e.tip)}</p>`
        : "";
      const weakHtml = e.weaknesses.length
        ? `<details class="set-cp-weak"><summary>Their weaknesses (${e.weaknesses.length})</summary>
             <ul>${e.weaknesses.map((w) => `<li>${esc(w)}</li>`).join("")}</ul></details>`
        : "";
      const customFields = e.isCustom
        ? `<div class="set-field">
             <label>Name</label>
             <input class="set-input" type="text" maxlength="80" data-role="c-label" value="${esc(e.label)}" />
           </div>
           <div class="set-field">
             <label>Also matches (comma-separated)</label>
             <input class="set-input" type="text" maxlength="200" data-role="c-aliases" value="${esc(e.aliases.join(", "))}" />
           </div>`
        : "";
      return `
        <div class="set-cp-card${e.enabled ? "" : " is-off"}" data-key="${esc(e.key)}">
          <div class="set-cp-card-head">
            <span class="set-cp-card-name">${esc(e.label)}</span>
            <span class="set-cp-badge ${e.isCustom ? "is-custom" : "is-default"}">${e.isCustom ? "Yours" : "TKOS"}</span>
            <label class="set-cp-toggle" title="Switch this competitor on or off for Kit">
              <input type="checkbox" data-role="toggle" ${e.enabled ? "checked" : ""} />
              <span>${e.enabled ? "On" : "Off"}</span>
            </label>
          </div>
          ${tipHtml}
          ${weakHtml}
          ${customFields}
          <div class="set-field">
            <label>Your talking points</label>
            <textarea class="set-input set-cp-textarea" rows="3" maxlength="4000"
              placeholder="Add your own local knowledge…" data-role="c-points">${esc(e.talkingPoints)}</textarea>
          </div>
          <div class="set-cp-card-actions">
            <button type="button" class="set-btn set-btn-sm" data-role="save">Save</button>
            ${e.isCustom ? `<button type="button" class="set-btn set-btn-danger set-btn-sm" data-role="delete">Delete</button>` : ""}
          </div>
        </div>
      `;
    }

    function renderList(): void {
      listEl.innerHTML = entries.length
        ? entries.map(cardHtml).join("")
        : `<div class="set-cp-empty">Couldn't load the playbook.</div>`;
    }

    async function load(): Promise<void> {
      try {
        const r = await ctx.api<{ ok: boolean; playbook: PBEntry[] }>(
          "/portal/competitor-playbook", { signal: ac.signal });
        if (disposed) return;
        entries = Array.isArray(r.playbook) ? r.playbook : [];
        renderList();
      } catch {
        if (disposed) return;
        listEl.innerHTML = `<div class="set-cp-empty">Couldn't load the playbook.</div>`;
      }
    }

    async function put(key: string, body: Record<string, unknown>): Promise<void> {
      await ctx.api(`/portal/competitor-playbook/${encodeURIComponent(key)}`, {
        method: "PUT", body: JSON.stringify(body), signal: ac.signal,
      });
    }

    // ── Card interactions (delegated) ──────────────────────────────────────────
    listEl.addEventListener("click", (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      const card = target.closest<HTMLElement>(".set-cp-card");
      if (!card) return;
      const key = card.dataset["key"]!;
      const entry = entries.find((x) => x.key === key);
      if (!entry) return;

      if (target.closest('[data-role="save"]')) {
        const points = card.querySelector<HTMLTextAreaElement>('[data-role="c-points"]')!.value.trim();
        const body: Record<string, unknown> = { talkingPoints: points };
        if (entry.isCustom) {
          body["label"] = card.querySelector<HTMLInputElement>('[data-role="c-label"]')!.value.trim();
          body["aliases"] = card.querySelector<HTMLInputElement>('[data-role="c-aliases"]')!.value;
        }
        void (async () => {
          try { await put(key, body); ctx.showToast("Saved", "success"); await load(); }
          catch { ctx.showToast("Couldn't save — please try again.", "error"); }
        })();
      } else if (target.closest('[data-role="delete"]')) {
        void (async () => {
          try {
            await ctx.api(`/portal/competitor-playbook/${encodeURIComponent(key)}`, { method: "DELETE", signal: ac.signal });
            ctx.showToast("Competitor removed", "success");
            await load();
          } catch { ctx.showToast("Couldn't remove it.", "error"); }
        })();
      }
    });

    listEl.addEventListener("change", (e) => {
      const target = e.target;
      if (!(target instanceof HTMLInputElement) || target.dataset["role"] !== "toggle") return;
      const card = target.closest<HTMLElement>(".set-cp-card");
      if (!card) return;
      const key = card.dataset["key"]!;
      const enabled = target.checked;
      void (async () => {
        try { await put(key, { enabled }); card.classList.toggle("is-off", !enabled); }
        catch { ctx.showToast("Couldn't update.", "error"); target.checked = !enabled; }
      })();
    });

    // ── Add a custom competitor ────────────────────────────────────────────────
    const addName = container.querySelector<HTMLInputElement>('[data-role="add-name"]')!;
    const addAliases = container.querySelector<HTMLInputElement>('[data-role="add-aliases"]')!;
    const addPoints = container.querySelector<HTMLTextAreaElement>('[data-role="add-points"]')!;
    const addErr = container.querySelector<HTMLElement>('[data-role="add-error"]')!;
    const addBtn = container.querySelector<HTMLButtonElement>('[data-role="add-save"]')!;
    addBtn.addEventListener("click", () => {
      const label = addName.value.trim();
      if (!label) { addErr.hidden = false; addErr.textContent = "Please enter a name."; return; }
      addErr.hidden = true;
      addBtn.disabled = true;
      void (async () => {
        try {
          await ctx.api("/portal/competitor-playbook", {
            method: "POST", signal: ac.signal,
            body: JSON.stringify({ label, aliases: addAliases.value, talkingPoints: addPoints.value.trim() }),
          });
          addName.value = ""; addAliases.value = ""; addPoints.value = "";
          ctx.showToast("Competitor added", "success");
          await load();
        } catch (err) {
          addErr.hidden = false;
          addErr.textContent = err instanceof ApiError ? "Couldn't add — please try again." : "Couldn't add the competitor.";
        } finally { addBtn.disabled = false; }
      })();
    });

    void load();
    return () => { disposed = true; ac.abort(); };
  },
};

export default competitorPlaybookTab;
